import React, { useState, useEffect } from 'react';
import { apiConfirmOvertimePay, apiGetGroups, apiGetEmployeesByGroup, apiPreviewOvertimePay } from '../../api';
import { message, Select } from 'antd';
import { ClipLoader } from 'react-spinners';

const { Option } = Select;

/**
 * Helper function to format numbers with dot separators for thousands.
 * Zeros are returned as '-'.
 */
const formatDisplayNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || Number(value) === 0) return '-';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};


const OvertimePay = () => {
    // --- State Management ---
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [skippedDetails, setSkippedDetails] = useState([]);
    
    const [isQuerying, setIsQuerying] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const today = new Date();
    // Change: Set default month to the current month instead of the last month.
    const defaultYymm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

    const [selectedMonth, setSelectedMonth] = useState(defaultYymm);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [selectedEmp, setSelectedEmp] = useState('ALL');

    // --- Data Fetching ---
    useEffect(() => {
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const groupsData = await apiGetGroups();
                setGroups(groupsData);
            } catch (error) {
                message.error('Không thể tải danh sách bộ phận.');
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, []);
    
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!selectedMonth || !selectedGroup) { // Check for selectedGroup as well
                setEmployees([]);
                setSelectedEmp('ALL');
                return;
            }
            const yymm = selectedMonth.replace('-', '');
            setLoadingEmployees(true);
            try {
                // Pass selectedGroup to the API call
                const data = await apiGetEmployeesByGroup(selectedGroup, yymm);
                setEmployees(data);
            } catch (error) {
                message.error('Không thể tải danh sách nhân viên.');
            } finally {
                setLoadingEmployees(false);
            }
        };
        fetchEmployees();
    }, [selectedMonth, selectedGroup]);


    // --- Handlers ---
    const handleGroupChange = (groupid) => {
        setSelectedGroup(groupid);
        setSelectedEmp('ALL'); // Reset employee selection when group changes
        setPreviewData([]);
        setSkippedDetails([]);
    };

    const handleQuery = async () => {
        if (!selectedMonth) {
            message.warn('Vui lòng chọn tháng tính lương.');
            return;
        }
        setIsQuerying(true);
        setPreviewData([]);
        setSkippedDetails([]);
        try {
            const payload = { yymm: selectedMonth, empid: selectedEmp, groupid: selectedGroup };
            const result = await apiPreviewOvertimePay(payload);
            setPreviewData(result.previewData || []);
            setSkippedDetails(result.skippedDetails || []);
            if (result.previewData.length === 0 && result.skippedDetails.length === 0) {
                message.info('Không tìm thấy nhân viên có giờ tăng ca trong kỳ.');
            }
        } catch (error) {
            message.error(error.message || 'Có lỗi xảy ra khi tra cứu.');
        } finally {
            setIsQuerying(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = { yymm: selectedMonth, empid: selectedEmp, groupid: selectedGroup };
            const result = await apiConfirmOvertimePay(payload);
            message.success(result.message || 'Xử lý thành công!');
            setPreviewData([]); // Clear table after successful save
            setSkippedDetails([]);
        } catch (error) {
            message.error(error.message || 'Có lỗi xảy ra khi lưu.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Table Definitions ---
    const tableHeaders = [
        { key: 'empid', label: 'MSNV', isFixed: 'left', width: '90px', style: { left: 0 } },
        { key: 'empName', label: 'Họ và Tên', isFixed: 'left', width: '180px', style: { left: '90px' } },
        { key: 'hourlyRate', label: 'Lương Giờ', width: '110px', isNumeric: true },
        { key: 'H1', label: 'Giờ 1.5', width: '70px', isNumeric: true },
        { key: 'H2', label: 'Giờ 2.0', width: '70px', isNumeric: true },
        { key: 'H3', label: 'Giờ 3.0', width: '70px', isNumeric: true },
        { key: 'B3', label: 'Giờ 2.1', width: '70px', isNumeric: true },
        { key: 'B4', label: 'PC 0.5(H)', width: '80px', isNumeric: true },
        { key: 'B5', label: 'PC 0.3(H)', width: '80px', isNumeric: true },
        { key: 'H1M', label: 'Tiền 150%', width: '130px', isNumeric: true },
        { key: 'H2M', label: 'Tiền 200%', width: '130px', isNumeric: true },
        { key: 'H3M', label: 'Tiền 300%', width: '130px', isNumeric: true },
        { key: 'B3M', label: 'Tiền 210%', width: '130px', isNumeric: true },
        { key: 'B4M', label: 'Tiền PC 0.5', width: '130px', isNumeric: true },
        { key: 'B5M', label: 'Tiền PC 0.3', width: '130px', isNumeric: true },
        { key: 'total', label: 'Tổng Tăng Ca', isFixed: 'right', width: '150px', style: { right: 0 }, isNumeric: true },
    ];

    const renderCellContent = (row, header) => {
        const value = row[header.key];
        const colorClasses = {
            hourlyRate: 'bg-yellow-100 text-yellow-800',
            H1M: 'bg-blue-100 text-blue-800',
            H2M: 'bg-cyan-100 text-cyan-800',
            H3M: 'bg-purple-100 text-purple-800',
            B3M: 'bg-indigo-100 text-indigo-800',
            B4M: 'bg-green-100 text-green-800',
            B5M: 'bg-pink-100 text-pink-800',
            total: 'bg-red-100 text-red-800 font-bold',
        };

        if (header.key === 'total') {
            const total = (row.H1M || 0) + (row.H2M || 0) + (row.H3M || 0) + (row.B3M || 0) + (row.B4M || 0) + (row.B5M || 0);
            return <span className={`px-2 py-1 rounded-md ${colorClasses[header.key]}`}>{formatDisplayNumber(total)}</span>;
        }

        if (header.isNumeric) {
             return <span className={`px-2 py-1 rounded-md ${colorClasses[header.key] || ''}`}>{formatDisplayNumber(value)}</span>
        }
        return <div className="px-2">{value}</div>;
    };
    
    const filterOption = (input, option) => {
        const optionText = Array.isArray(option.children) ? option.children.join('') : option.children;
        return (optionText || '').toLowerCase().includes(input.toLowerCase());
    };

    return (
        <div style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Xem & Xác Nhận Lương Tăng Ca</h2>
            <p className="text-gray-600 mb-6">Tra cứu dữ liệu tăng ca đã được tính toán để xem trước, sau đó xác nhận để lưu vào hệ thống.</p>
            
             <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                {/* Filters */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Tháng tính lương</label>
                    <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="mt-1 p-2 border rounded-md w-full" style={{ fontFamily: 'inherit' }} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Bộ phận</label>
                    <Select
                        showSearch
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        loading={loadingGroups}
                        style={{ width: 240, fontFamily: 'inherit' }}
                        className="mt-1"
                        placeholder="Tìm và chọn bộ phận"
                        optionFilterProp="children"
                        filterOption={filterOption}
                    >
                        <Option value="ALL">-- Tất cả bộ phận --</Option>
                        {groups.map(g => <Option key={g.groupId} value={g.groupId}>{g.groupName} ({g.groupId})</Option>)}
                    </Select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Nhân viên</label>
                    <Select
                        showSearch
                        value={selectedEmp}
                        onChange={(value) => setSelectedEmp(value)}
                        loading={loadingEmployees}
                        disabled={loadingEmployees}
                        style={{ width: 240, fontFamily: 'inherit' }}
                        placeholder="Tìm và chọn nhân viên"
                        optionFilterProp="children"
                        filterOption={filterOption}
                        className="mt-1"
                    >
                        <Option value="ALL">-- Tất cả nhân viên --</Option>
                        {employees.map(e => <Option key={e.empid} value={e.empid}>{e.name} ({e.empid})</Option>)}
                    </Select>
                </div>
                {/* Action Buttons */}
                <div className="flex items-center gap-4">
                    <button onClick={handleQuery} disabled={isQuerying || isSaving} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                        {isQuerying ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Đang tải...</> : 'Tra cứu dữ liệu'}
                    </button>
                    <button onClick={handleSave} disabled={isSaving || isQuerying || previewData.length === 0} className="px-6 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400">
                        {isSaving ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Đang lưu...</> : 'Xác nhận & Lưu'}
                    </button>
                </div>
            </div>

            {/* Results Section */}
            {(isQuerying || previewData.length > 0 || skippedDetails.length > 0) && (
                 <div className="mt-6">
                    {skippedDetails.length > 0 && (
                        <div className="mb-4 p-4 border rounded-lg bg-red-50">
                            <h4 className="font-semibold text-red-700">Các nhân viên bị bỏ qua (do thiếu lương cơ bản):</h4>
                            <ul className="list-disc list-inside mt-2 text-sm text-gray-700">
                                {skippedDetails.map(item => <li key={item.empid}><strong>{item.empName} ({item.empid}):</strong> {item.reason}</li>)}
                            </ul>
                        </div>
                    )}
                    
                    <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '70vh' }}>
                        <table className="min-w-full text-sm">
                           <thead>
                                <tr>
                                    {tableHeaders.map(header => (
                                        <th key={header.key} 
                                            className={`p-2 font-semibold text-gray-600 whitespace-nowrap border-b border-r sticky top-0 bg-gray-100 ${header.isFixed ? 'z-20' : 'z-10'} ${header.isNumeric ? 'text-right' : 'text-left'}`}
                                            style={{ width: header.width, minWidth: header.width, ...header.style }}>
                                            {header.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                             <tbody className="divide-y divide-gray-200">
                                {isQuerying ? (
                                    <tr><td colSpan={tableHeaders.length} className="p-4 text-center">Đang tải dữ liệu...</td></tr>
                                ) : previewData.map(row => (
                                    <tr key={row.empid} className="hover:bg-gray-50">
                                        {tableHeaders.map(header => (
                                            <td key={`${row.empid}-${header.key}`}
                                                className={`p-1 whitespace-nowrap border-r ${header.isFixed ? 'sticky bg-white hover:bg-gray-50' : ''} ${header.isNumeric ? 'text-right' : 'text-left'}`}
                                                style={{ ...header.style }}>
                                                {renderCellContent(row, header)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OvertimePay;
